// src/features/MyGymTV/UK/DSDetails/DSDetails.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../../../layout/Layout';
import { Card, Form, Button, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTrash, faSave, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import './ds-details.scss';

const DSDetails = () => {
    const navigate = useNavigate();
    const { contentId } = useParams();
    const { selectedClubs } = useSelector((state) => state.ukEntities);
    const allAccessibleClubs = useSelector((state) => state.user.ukEntityData || []);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const [contentDetails, setContentDetails] = useState(null);
    const [mediaUrl, setMediaUrl] = useState(null);
    const [filename, setFilename] = useState('');

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [displaySeconds, setDisplaySeconds] = useState(10);
    const [isActive, setIsActive] = useState(true);

    const [originalValues, setOriginalValues] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [affectedVenues, setAffectedVenues] = useState([]);

    const [showAddVenuesModal, setShowAddVenuesModal] = useState(false);
    const [availableVenues, setAvailableVenues] = useState([]);
    const [selectedNewVenues, setSelectedNewVenues] = useState([]);

    useEffect(() => {
        if (selectedClubs.length === 0) {
            setError('Please select venues to edit content');
            setLoading(false);
            return;
        }

        fetchContentDetails();
    }, [contentId, selectedClubs]);

    useEffect(() => {
        if (!originalValues) return;

        const changed =
            startDate !== originalValues.startDate ||
            endDate !== originalValues.endDate ||
            displaySeconds !== originalValues.displaySeconds ||
            isActive !== originalValues.isActive;

        setHasChanges(changed);
    }, [startDate, endDate, displaySeconds, isActive, originalValues]);

    const fetchContentDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const clubIds = selectedClubs.map(c => c.ClubId);

            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/UK/GetDSContentDetails`,
                { contentId: parseInt(contentId), clubIds },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data && response.data.length > 0) {
                const content = response.data[0];
                const startDateStr = content.StartDate ? moment(content.StartDate).format('YYYY-MM-DD') : '';
                const endDateStr = content.EndDate ? moment(content.EndDate).format('YYYY-MM-DD') : '';

                setContentDetails(response.data);
                setFilename(content.Filename || 'Unknown');
                setStartDate(startDateStr);
                setEndDate(endDateStr);
                setDisplaySeconds(content.DisplaySeconds || 10);
                setIsActive(content.IsActive);

                setOriginalValues({
                    startDate: startDateStr,
                    endDate: endDateStr,
                    displaySeconds: content.DisplaySeconds || 10,
                    isActive: content.IsActive
                });

                fetchMediaUrl(parseInt(contentId));
            } else {
                setError('Content not found for selected venues');
            }

        } catch (err) {
            console.error('Error fetching content details:', err);
            setError('Failed to load content details');
        } finally {
            setLoading(false);
        }
    };

    const fetchMediaUrl = async (contentID) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/DigitalSignage/${contentID}/content`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setMediaUrl(url);
        } catch (error) {
            console.error('Error fetching media:', error);
        }
    };

    const handleSave = () => {
        const venues = contentDetails.map(item => ({
            VenueID: item.VenueID,
            VenueName: selectedClubs.find(c => c.ClubId === item.VenueID)?.ClubName
        }));

        setAffectedVenues(venues);
        setConfirmAction('save');
        setShowConfirmModal(true);
    };

    const handleDelete = () => {
        const venues = contentDetails.map(item => ({
            VenueID: item.VenueID,
            VenueName: selectedClubs.find(c => c.ClubId === item.VenueID)?.ClubName
        }));

        setAffectedVenues(venues);
        setConfirmAction('delete');
        setShowConfirmModal(true);
    };

    const confirmSave = async () => {
        try {
            setSaving(true);
            setShowConfirmModal(false);
            const token = localStorage.getItem('token');

            const updateData = {
                contentId: parseInt(contentId),
                clubIds: selectedClubs.map(c => c.ClubId),
                startDate,
                endDate,
                displaySeconds,
                isActive
            };

            await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/UK/UpdateDSContent`,
                updateData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            setSuccessMessage('Content updated successfully');
            setTimeout(() => {
                navigate('/uk/review-ds');
            }, 1500);

        } catch (err) {
            console.error('Error updating content:', err);
            setError('Failed to update content');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        try {
            setSaving(true);
            setShowConfirmModal(false);
            const token = localStorage.getItem('token');

            await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/UK/DeleteDSContent`,
                {
                    contentId: parseInt(contentId),
                    clubIds: selectedClubs.map(c => c.ClubId)
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            setSuccessMessage('Content deleted successfully');
            setTimeout(() => {
                navigate('/uk/review-ds');
            }, 1500);

        } catch (err) {
            console.error('Error deleting content:', err);
            setError('Failed to delete content');
        } finally {
            setSaving(false);
        }
    };

    const fetchAvailableVenues = () => {
        const currentVenueIds = contentDetails.map(item => item.VenueID);
        const available = allAccessibleClubs.filter(club => !currentVenueIds.includes(club.ClubId));

        setAvailableVenues(available);
        setShowAddVenuesModal(true);
    };

    const handleAddVenues = async () => {
        try {
            setSaving(true);
            const token = localStorage.getItem('token');

            await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/UK/AddContentToVenues`,
                {
                    contentId: parseInt(contentId),
                    clubIds: selectedNewVenues,
                    startDate,
                    endDate,
                    displaySeconds,
                    isActive
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            setShowAddVenuesModal(false);
            setSelectedNewVenues([]);
            setSuccessMessage('Content added to selected venues');

            fetchContentDetails();

        } catch (err) {
            console.error('Error adding venues:', err);
            setError('Failed to add content to venues');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="ds-details-container mt-5 text-center">
                    <FontAwesomeIcon
icon={faSpinner}
size="3x"
spin
                    />
                    <p className="mt-3">Loading content...</p>
                </div>
            </Layout>
        );
    }

    if (error && !contentDetails) {
        return (
            <Layout>
                <div className="ds-details-container mt-5">
                    <Alert variant="danger">{error}</Alert>
                    <Button onClick={() => navigate('/uk/review-ds')}>
                        <FontAwesomeIcon
className="me-2"
icon={faArrowLeft}
                        />
                        Back to Review
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="ds-details-container mt-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4>Edit Digital Signage Content</h4>
                    <Button
onClick={() => navigate('/uk/review-ds')}
variant="secondary"
                    >
                        <FontAwesomeIcon
className="me-2"
icon={faArrowLeft}
                        />
                        Back to Review
                    </Button>
                </div>

                {successMessage && (
                    <Alert
dismissible
onClose={() => setSuccessMessage(null)}
variant="success"
                    >
                        {successMessage}
                    </Alert>
                )}

                {error && (
                    <Alert
dismissible
onClose={() => setError(null)}
variant="danger"
                    >
                        {error}
                    </Alert>
                )}

                <Card>
                    <Card.Body>
                        <div className="row">
                            <div className="col-md-6">
                                <h5 className="mb-3">Content Preview</h5>
                                {mediaUrl ? (
                                    <img
alt="Content"
className="img-fluid rounded"
src={mediaUrl}
                                    />
                                ) : (
                                    <div className="text-center p-5">
                                        <FontAwesomeIcon
icon={faSpinner}
size="2x"
spin
                                        />
                                    </div>
                                )}

                                <div className="mt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <strong>Affected Venues & Orders:</strong>
                                        <Button
                                            onClick={fetchAvailableVenues}
                                            size="sm"
                                            variant="outline-primary"
                                        >
                                            + Add Venues
                                        </Button>
                                    </div>
                                    <table className="table table-sm mt-2">
                                        <thead>
                                            <tr>
                                                <th>Venue</th>
                                                <th>Order Name</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {contentDetails?.map(item => (
                                                <tr key={item.VenueID}>
                                                    <td>{selectedClubs.find(c => c.ClubId === item.VenueID)?.ClubName}</td>
                                                    <td>{item.OrderName}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <h5 className="mb-3">Content Details</h5>
                                <Form>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Filename</Form.Label>
                                        <Form.Control
                                            disabled
                                            readOnly
                                            type="text"
                                            value={filename}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Start Date</Form.Label>
                                        <Form.Control
                                            onChange={(e) => setStartDate(e.target.value)}
                                            type="date"
                                            value={startDate}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>End Date</Form.Label>
                                        <Form.Control
                                            onChange={(e) => setEndDate(e.target.value)}
                                            type="date"
                                            value={endDate}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Display Seconds</Form.Label>
                                        <Form.Control
                                            max="60"
                                            min="1"
                                            onChange={(e) => setDisplaySeconds(parseInt(e.target.value))}
                                            type="number"
                                            value={displaySeconds}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            checked={isActive}
                                            label="Active"
                                            onChange={(e) => setIsActive(e.target.checked)}
                                            type="checkbox"
                                        />
                                    </Form.Group>

                                    <div className="d-flex gap-2">
                                        <Button
                                            disabled={saving || !hasChanges}
                                            onClick={handleSave}
                                            variant="primary"
                                        >
                                            <FontAwesomeIcon
className="me-2"
icon={faSave}
                                            />
                                            {saving ? 'Saving...' : 'Save Changes'}
                                        </Button>

                                        <Button
                                            disabled={saving}
                                            onClick={handleDelete}
                                            variant="danger"
                                        >
                                            <FontAwesomeIcon
className="me-2"
icon={faTrash}
                                            />
                                            Delete
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {/* Confirmation Modal */}
                <Modal
onHide={() => setShowConfirmModal(false)}
show={showConfirmModal}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {confirmAction === 'delete' ? 'Confirm Delete' : 'Confirm Save'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            This action will {confirmAction === 'delete' ? 'delete' : 'update'} this content for the following venue(s):
                        </p>
                        <ul>
                            {affectedVenues.map(venue => (
                                <li key={venue.VenueID}>{venue.VenueName}</li>
                            ))}
                        </ul>
                        <p className="mb-0">
                            {confirmAction === 'delete'
                                ? 'This will permanently remove the content from these venues. Are you sure?'
                                : 'Are you sure you want to save these changes?'}
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
onClick={() => setShowConfirmModal(false)}
variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmAction === 'delete' ? confirmDelete : confirmSave}
                            variant={confirmAction === 'delete' ? 'danger' : 'primary'}
                        >
                            {confirmAction === 'delete' ? 'Delete' : 'Save Changes'}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Add Venues Modal */}
                <Modal
onHide={() => setShowAddVenuesModal(false)}
show={showAddVenuesModal}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Add Content to Additional Venues</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {availableVenues.length === 0 ? (
                            <Alert variant="info">
                                This content is already assigned to all available venues.
                            </Alert>
                        ) : (
                            <>
                                <p>Select venues to add this content to:</p>
                                {availableVenues.map(venue => (
                                    <Form.Check
                                        checked={selectedNewVenues.includes(venue.ClubId)}
                                        id={`venue-${venue.ClubId}`}
                                        key={venue.ClubId}
                                        label={venue.ClubName}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedNewVenues([...selectedNewVenues, venue.ClubId]);
                                            } else {
                                                setSelectedNewVenues(selectedNewVenues.filter(id => id !== venue.ClubId));
                                            }
                                        }}
                                        type="checkbox"
                                    />
                                ))}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
onClick={() => setShowAddVenuesModal(false)}
variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={selectedNewVenues.length === 0 || saving}
                            onClick={handleAddVenues}
                            variant="primary"
                        >
                            Add to {selectedNewVenues.length} Venue(s)
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </Layout>
    );
};

export default DSDetails;